import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../services/api';
import { getAllDepartments, getDepartmentForCategory } from '../utils/departmentMapping';
import { formatDateTime, getDateInputValue, parseServerDate } from '../utils/dateUtils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend
} from 'recharts';
import {
  FiUsers,
  FiCheckCircle,
  FiFilter,
  FiRefreshCw,
  FiHome,
  FiAlertTriangle,
  FiSearch,
  FiDownload,
  FiX,
  FiChevronRight,
  FiStar,
  FiAward,
  FiFileText
} from 'react-icons/fi';
import { exportAnalyticsPdf } from '../utils/pdfExportUtils';


/* ─── Helpers & Palette ───────────────────────────────── */
const PALETTE = ['#6ec7c8', '#f5c542', '#ff66c4', '#c164d8', '#ffc2c2', '#4ecdc4', '#ffd166', '#f78fb3'];

const downloadCSV = (filename, headers, rows) => {
  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((val) => `"${String(val ?? '').replace(/"/g, '""')}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const getCleanDepartmentName = (dept, cat) => {
  const d = String(dept || '').toLowerCase();
  const c = String(cat || '').toLowerCase();

  if (d.includes('housekeeping') || c.includes('clean') || c.includes('towel') || c.includes('toiletr') || c.includes('linen') || c.includes('laundry')) return 'Housekeeping';
  if (d.includes('maint') || c.includes('maint') || c.includes('repair') || c.includes('ac ') || c.includes('aircon') || c.includes('plumb') || c.includes('leak') || c.includes('light')) return 'Maintenance';
  if (d.includes('front') || d.includes('concierge') || c.includes('front') || c.includes('checkout') || c.includes('luggage') || c.includes('cab') || c.includes('taxi')) return 'Front Office';
  if (d.includes('dining') || d.includes('restaurant') || d.includes('food') || c.includes('food') || c.includes('dining') || c.includes('drink') || c.includes('order')) return 'Food & Beverage';
  if (d.includes('it') || c.includes('wifi') || c.includes('tv') || c.includes('internet') || c.includes('tech')) return 'IT Support';
  if (d.includes('spa') || d.includes('wellness') || d.includes('massage') || c.includes('spa') || c.includes('yoga') || c.includes('salon')) return 'Spa & Wellness';
  if (d.includes('recreation') || d.includes('pickle') || c.includes('padel') || c.includes('pickle')) return 'Recreation';

  return dept && dept !== 'Unmapped' ? dept : 'Other';
};

const CANONICAL_CATEGORIES = {
  'front_office': 'Front Office',
  'front office': 'Front Office',
  'frontoffice': 'Front Office',
  'it_support': 'IT Support',
  'it support': 'IT Support',
  'itsupport': 'IT Support',
  'housekeeping': 'Housekeeping Service',
  'housekeeping_service': 'Housekeeping Service',
  'housekeeping service': 'Housekeeping Service',
  'maintenance': 'Maintenance Assistance',
  'maintenance_assistance': 'Maintenance Assistance',
  'maintenance assistance': 'Maintenance Assistance',
  'room_cleaning': 'Room Cleaning',
  'room cleaning': 'Room Cleaning',
  'room_service': 'Room Service',
  'room service': 'Room Service',
  'toiletries & supplies': 'Toiletries & Supplies',
  'toiletries and supplies': 'Toiletries & Supplies',
  'toiletries_supplies': 'Toiletries & Supplies',
  'medical': 'Medical Emergency',
  'medical_emergency': 'Medical Emergency',
  'medical emergency': 'Medical Emergency',
  'security': 'Security Assistance',
  'security_assistance': 'Security Assistance',
  'security assistance': 'Security Assistance',
  'spa': 'Spa & Wellness',
  'spa_wellness': 'Spa & Wellness',
  'spa & wellness': 'Spa & Wellness',
  'spa appointment': 'Spa & Wellness',
  'yoga': 'Yoga & Wellness',
  'yoga_wellness': 'Yoga & Wellness',
  'yoga & wellness': 'Yoga & Wellness',
  'restaurant': 'Restaurant',
  'food & beverage': 'Restaurant',
  'food and beverage': 'Restaurant',
  'food_and_beverage': 'Restaurant',
  'dining': 'Restaurant',
  'pickleball': 'PickleBall',
  'padel': 'Padel'
};

const normalizeCategory = (category) => {
  if (!category) return 'Uncategorized';
  const trimmed = String(category).trim();
  const rawKey = trimmed.toLowerCase();
  const lower = rawKey.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

  if (CANONICAL_CATEGORIES[rawKey]) return CANONICAL_CATEGORIES[rawKey];
  if (CANONICAL_CATEGORIES[lower]) return CANONICAL_CATEGORIES[lower];

  return lower
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const normalizeStatus = (requestOrStatus) => {
  if (!requestOrStatus) return 'pending';
  // If it's a request object
  if (typeof requestOrStatus === 'object') {
    const r = requestOrStatus;
    const meta = r.metadata || {};
    const dispatchStatus = String(meta.dispatch_status || r.dispatch_status || '').toLowerCase();
    const completionResponse = String(meta.completion_response || r.completion_response || '').toLowerCase();
    const criticalStatus = String(meta.critical_fix_status || '').toLowerCase();
    const details = String(r.details || r.description || '').toLowerCase();
    const normStatus = String(r.status || '').toLowerCase();

    // 1. Service Dispute
    if (dispatchStatus === 'guest_disputed' || normStatus === 'dispute' || normStatus === 'service dispute' || normStatus === 'service_dispute') {
      return 'dispute';
    }

    // 2. Critical Fix
    if (
      criticalStatus !== 'cancelled' &&
      (dispatchStatus === 'critical_escalated' ||
       completionResponse === 'critical' ||
       r.is_critical === true ||
       criticalStatus === 'confirmed' ||
       criticalStatus === 'resolved' ||
       criticalStatus === 'review_pending' ||
       normStatus === 'critical' ||
       normStatus === 'critical_fix' ||
       normStatus === 'critical fix')
    ) {
      return 'critical';
    }

    // 3. DND
    if (
      completionResponse === 'dnd' ||
      dispatchStatus === 'dnd_notified' ||
      details.includes('dnd') ||
      normStatus === 'dnd'
    ) {
      return 'dnd';
    }

    // 4. Completed
    if (normStatus === 'completed' || ['yes', 'no'].includes(completionResponse)) {
      return 'completed';
    }

    // 5. Cancelled
    if (normStatus === 'cancelled') {
      return 'cancelled';
    }

    // 6. Default: Pending
    return 'pending';
  }

  // If a string status was passed
  const statusStr = String(requestOrStatus).toLowerCase();
  if (statusStr === 'completed') return 'completed';
  if (statusStr === 'cancelled') return 'cancelled';
  if (statusStr === 'dnd') return 'dnd';
  if (statusStr === 'critical' || statusStr === 'critical_fix' || statusStr === 'critical fix') return 'critical';
  if (statusStr === 'dispute' || statusStr === 'service dispute' || statusStr === 'service_dispute' || statusStr === 'guest_disputed') return 'dispute';
  if (['pending', 'received', 'in-progress'].includes(statusStr)) return 'pending';
  return statusStr;
};

const getDepartmentFromCategory = (category) => getDepartmentForCategory(category);

const getRangeDates = (range) => {
  if (range === 'all') return { startDate: null, endDate: null };
  const today = new Date();
  const todayStr = getDateInputValue(today);
  if (range === 'today') return { startDate: todayStr, endDate: todayStr };
  const endDate = todayStr;
  const start = new Date(today);
  if (range === '7d') start.setDate(start.getDate() - 6);
  if (range === '30d') start.setDate(start.getDate() - 29);
  if (range === '90d') start.setDate(start.getDate() - 89);
  return { startDate: getDateInputValue(start), endDate };
};

const quantile = (values, q) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  return sorted[base];
};

const isAutoRepliedOrExcluded = (request) => {
  if (!request) return false;
  const meta = request.metadata || {};
  if (meta.auto_reply === true || meta.auto_replied === true || meta.is_auto_reply === true || meta.auto_completed === true) {
    return true;
  }
  const cat = String(request.category || request.service_name || '').trim().toLowerCase();
  const details = String(request.details || request.description || '').trim().toLowerCase();
  const act = String(meta.requested_activity || '').trim().toLowerCase();

  if ((cat.includes('wifi') || cat.includes('wi-fi') || details.includes('wifi') || details.includes('wi-fi')) &&
      (details.includes('password') || details.includes('credentials') || meta.wifi_details)) {
    return true;
  }

  if (cat.includes('restaurant') || cat.includes('table booking') || details.includes('table booking') || details.includes('book table')) {
    return true;
  }

  const excludedActivities = ['yoga', 'spa', 'massage', 'wellness', 'cricket', 'badminton', 'tennis', 'squash', 'table tennis', 'gym', 'cycling', 'swimming', 'sports', 'recreation'];
  if (excludedActivities.some((a) => cat.includes(a) || details.includes(a) || act.includes(a))) {
    return true;
  }

  return false;
};

const formatHours = (hours) => {
  if (!hours || Number.isNaN(hours) || hours <= 0) return '0 min';
  const totalMinutes = Math.round(hours * 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
};

const getLifecycleStartAt = (request) => {
  const pendingAt = parseServerDate(request.pending_at);
  const createdAt = parseServerDate(request.created_at);
  if (pendingAt && createdAt) return pendingAt < createdAt ? pendingAt : createdAt;
  return pendingAt || createdAt;
};

const getLifecycleEndAt = (request) =>
  parseServerDate(request.completed_at || request.updated_at || request.created_at);

/* ─── UI Components ──────────────────────────────────── */
const Card = ({ children, style = {}, className = '' }) => (
  <div
    className={`rounded-[20px] border border-gray-100 bg-white p-5 shadow-xs transition-all hover:shadow-sm ${className}`}
    style={style}
  >
    {children}
  </div>
);

const MiniTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-gray-100 bg-white shadow-lg p-3 text-xs">
      <p className="font-bold text-gray-900 mb-1">{label}</p>
      {payload.map((e, i) => (
        <div key={i} className="flex items-center gap-2 text-gray-600">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: e.color || '#6ec7c8' }} />
          <span>{e.name}: <b className="text-gray-900">{typeof e.value === 'number' && e.value.toFixed ? e.value.toFixed(1) : e.value}</b></span>
        </div>
      ))}
    </div>
  );
};

/* ─── Main Analytics Component ────────────────────────── */
function Analytics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [operationsData, setOperationsData] = useState(null);

  const [filters, setFilters] = useState(() => {
    const urlTimeRange = searchParams.get('timeRange');
    const initialRange = urlTimeRange || '30d';
    const defaults = getRangeDates(initialRange);
    return {
      timeRange: initialRange,
      startDate: defaults.startDate,
      endDate: defaults.endDate,
      department: 'all',
      category: 'all',
      status: 'all'
    };
  });

  const [staffSectionFilters, setStaffSectionFilters] = useState({ department: 'all', staff: 'all' });
  const [roomSearchQuery, setRoomSearchQuery] = useState('');
  const [roomSortBy, setRoomSortBy] = useState('volume');
  const [roomFilterAlertOnly, setRoomFilterAlertOnly] = useState(false);
  const [selectedRoomModal, setSelectedRoomModal] = useState(null);
  const [selectedStaffModal, setSelectedStaffModal] = useState(null);
  const [staffModalSearch, setStaffModalSearch] = useState('');
  const [selectedDeptModal, setSelectedDeptModal] = useState(null);
  const [deptModalSearch, setDeptModalSearch] = useState('');
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const exportContainerRef = React.useRef(null);

  const fetchOperationsData = useCallback(async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const opsRes = await api.get('/api/admin/operations-analytics', { params }).catch(() => ({ data: null }));
      if (opsRes?.data) {
        setOperationsData(opsRes.data);
      }
    } catch (e) {
      console.error('Failed to fetch operations data:', e);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      const [reqsRes, opsRes] = await Promise.all([
        api.get('/api/roomservice/requests?limit=all'),
        api.get('/api/admin/operations-analytics', { params }).catch(() => ({ data: null }))
      ]);
      setRequests(Array.isArray(reqsRes.data) ? reqsRes.data : []);
      setOperationsData(opsRes?.data || null);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []); // Run once on mount

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // When filters change date range, update operationsData in the background without full-page spinner
  useEffect(() => {
    fetchOperationsData(filters.startDate, filters.endDate);
  }, [filters.startDate, filters.endDate, fetchOperationsData]);

  // Sync state if URL query params change (e.g. redirected from Dashboard or browser navigation)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    const timeRangeFromUrl = searchParams.get('timeRange');
    if (timeRangeFromUrl) {
      if (timeRangeFromUrl === 'custom') {
        const s = searchParams.get('startDate');
        const e = searchParams.get('endDate');
        setFilters((prev) => {
          if (prev.timeRange === 'custom' && prev.startDate === (s || null) && prev.endDate === (e || null)) {
            return prev;
          }
          return {
            ...prev,
            timeRange: 'custom',
            startDate: s || prev.startDate,
            endDate: e || prev.endDate
          };
        });
      } else {
        const { startDate, endDate } = getRangeDates(timeRangeFromUrl);
        setFilters((prev) => {
          if (prev.timeRange === timeRangeFromUrl && prev.startDate === startDate && prev.endDate === endDate) {
            return prev;
          }
          return {
            ...prev,
            timeRange: timeRangeFromUrl,
            startDate,
            endDate
          };
        });
      }
    }
  }, [searchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tabId);
      return next;
    });
  };

  const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const handleTimeRangeChange = (range) => {
    if (range === 'custom') {
      setFilters((prev) => ({ ...prev, timeRange: 'custom' }));
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('timeRange', 'custom');
        return next;
      });
      return;
    }
    const { startDate, endDate } = getRangeDates(range);
    setFilters((prev) => ({ ...prev, timeRange: range, startDate, endDate }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('timeRange', range);
      if (startDate) next.set('startDate', startDate);
      else next.delete('startDate');
      if (endDate) next.set('endDate', endDate);
      else next.delete('endDate');
      return next;
    });
  };

  const handleCustomDateChange = (field, val) => {
    const nextVal = val || null;
    setFilters((prev) => ({
      ...prev,
      timeRange: 'custom',
      [field]: nextVal
    }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('timeRange', 'custom');
      if (nextVal) next.set(field, nextVal);
      else next.delete(field);
      return next;
    });
  };

  const resetFilters = () => {
    const defaults = getRangeDates('30d');
    setFilters({ timeRange: '30d', startDate: defaults.startDate, endDate: defaults.endDate, department: 'all', category: 'all', status: 'all' });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('timeRange', '30d');
      next.delete('startDate');
      next.delete('endDate');
      return next;
    });
  };

  /* ── Filtered Requests ──────────────────────────────── */
  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      const createdDate = parseServerDate(r.created_at || r.createdAt);
      const completedDate = parseServerDate(r.completed_at || r.updated_at || r.updatedAt);

      const getISTDateStr = (dateObj) => {
        if (!dateObj) return '';
        try {
          return dateObj.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
        } catch {
          return getDateInputValue(dateObj);
        }
      };

      const createdStr = getISTDateStr(createdDate);
      const completedStr = getISTDateStr(completedDate);

      const isInRange = (dStr) => {
        if (!dStr) return false;
        if (filters.startDate && dStr < filters.startDate) return false;
        if (filters.endDate && dStr > filters.endDate) return false;
        return true;
      };

      if (filters.startDate || filters.endDate) {
        // Request belongs to the period if it was created in the range OR completed in the range
        if (!isInRange(createdStr) && !isInRange(completedStr)) {
          return false;
        }
      }

      const cat = normalizeCategory(r.category || r.service_name);
      const dept = getCleanDepartmentName(getDepartmentFromCategory(cat), cat);
      if (filters.department !== 'all' && dept !== filters.department) return false;
      if (filters.category !== 'all' && cat !== filters.category) return false;
      const status = normalizeStatus(r);
      if (filters.status !== 'all' && status !== filters.status) return false;
      return true;
    });
  }, [requests, filters]);

  /* ── Categories List ────────────────────────────────── */
  const categories = useMemo(() => {
    const set = new Set();
    requests.forEach((r) => {
      const cat = normalizeCategory(r.category || r.service_name);
      if (cat) set.add(cat);
    });
    return ['all', ...Array.from(set).sort()];
  }, [requests]);

  /* ── Core Computed Analytics ────────────────────────── */
  const analytics = useMemo(() => {
    const totalRequests = filteredRequests.length;
    let completedCount = 0;
    let pendingCount = 0;
    let dndCount = 0;
    let criticalCount = 0;
    let disputeCount = 0;
    let cancelledCount = 0;

    const categoryMap = {};
    const departmentMap = {};
    const completionDurations = [];
    const trendMap = {};

    filteredRequests.forEach((r) => {
      const st = normalizeStatus(r);
      if (st === 'completed') completedCount += 1;
      else if (st === 'dnd') dndCount += 1;
      else if (st === 'critical') criticalCount += 1;
      else if (st === 'dispute') disputeCount += 1;
      else if (st === 'cancelled') cancelledCount += 1;
      else pendingCount += 1;

      const cat = normalizeCategory(r.category || r.service_name);
      categoryMap[cat] = (categoryMap[cat] || 0) + 1;

      const dept = getCleanDepartmentName(getDepartmentFromCategory(cat), cat);
      if (!departmentMap[dept]) {
        departmentMap[dept] = {
          total: 0,
          completed: 0,
          pending: 0,
          dnd: 0,
          critical: 0,
          dispute: 0,
          cancelled: 0,
          durations: [],
          requests: []
        };
      }
      departmentMap[dept].total += 1;
      if (st === 'completed') departmentMap[dept].completed += 1;
      else if (st === 'dnd') departmentMap[dept].dnd += 1;
      else if (st === 'critical') departmentMap[dept].critical += 1;
      else if (st === 'dispute') departmentMap[dept].dispute += 1;
      else if (st === 'cancelled') departmentMap[dept].cancelled += 1;
      else departmentMap[dept].pending += 1;

      const s = getLifecycleStartAt(r);
      const e = getLifecycleEndAt(r);
      let diffMinutes = null;
      if (s && e && e >= s) {
        diffMinutes = Math.round((e.getTime() - s.getTime()) / 60000);
      }

      departmentMap[dept].requests.push({
        id: r.id || r._id,
        room_no: r.room_number || r.roomNumber || r.room_no || r.guests?.room_no || '—',
        guest_name: r.guests?.name || r.guest_name || 'Guest',
        category: cat,
        department: dept,
        status: st,
        details: r.details || r.description || 'Service Request',
        staff_name: r.assigned_to_name || r.completed_by_staff_name || r.metadata?.completed_by_staff_name || r.metadata?.assigned_staff_name || null,
        created_at: r.created_at || r.createdAt,
        completed_at: r.completed_at || r.updated_at || r.updatedAt,
        diffMinutes,
        rating: r.rating || r.guest_rating || r.metadata?.service_rating || null,
        metadata: r.metadata
      });

      const created = parseServerDate(r.created_at || r.createdAt);
      if (created) {
        const dateKey = created.toISOString().split('T')[0];
        if (!trendMap[dateKey]) trendMap[dateKey] = { date: dateKey, created: 0, completed: 0 };
        trendMap[dateKey].created += 1;
        if (st === 'completed') trendMap[dateKey].completed += 1;
      }

      if (st === 'completed' && !isAutoRepliedOrExcluded(r)) {
        if (s && e && e >= s) {
          const hours = (e.getTime() - s.getTime()) / (1000 * 60 * 60);
          if (hours >= 0 && hours < 240) {
            completionDurations.push(hours);
            departmentMap[dept].durations.push(hours);
          }
        }
      }
    });

    const completionRate = totalRequests > 0 ? (completedCount / totalRequests) * 100 : 0;
    const avgCompletionHours =
      completionDurations.length > 0
        ? completionDurations.reduce((a, b) => a + b, 0) / completionDurations.length
        : 0;
    const medianCompletionHours = quantile(completionDurations, 0.5);
    const sla4hRate =
      completionDurations.length > 0
        ? (completionDurations.filter((h) => h <= 4).length / completionDurations.length) * 100
        : 0;

    const requestsByCategory = Object.entries(categoryMap)
      .map(([category, count]) => ({
        category,
        count,
        percentage: totalRequests > 0 ? Math.round((count / totalRequests) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count);

    const departmentStats = Object.entries(departmentMap).map(([dept, data]) => {
      const avgH = data.durations.length > 0 ? data.durations.reduce((a, b) => a + b, 0) / data.durations.length : 0;
      const rate = data.total > 0 ? (data.completed / data.total) * 100 : 0;
      return {
        department: dept,
        total: data.total,
        completed: data.completed,
        pending: data.pending,
        dnd: data.dnd,
        critical: data.critical,
        dispute: data.dispute,
        cancelled: data.cancelled,
        completionRate: rate,
        avgCompletionHours: avgH,
        avgCompletionMinutes: Math.max(0, Math.round(avgH * 60)),
        requests: data.requests || []
      };
    }).sort((a, b) => b.total - a.total);

    const trendData = Object.values(trendMap)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => ({
        ...d,
        label: d.date.slice(5)
      }));

    const requestsByStatus = [
      { name: 'Completed', count: completedCount, color: '#10b981' },
      { name: 'Pending', count: pendingCount, color: '#f59e0b' },
      { name: 'DND', count: dndCount, color: '#64748b' },
      { name: 'Critical Fix', count: criticalCount, color: '#ef4444' },
      { name: 'Service Dispute', count: disputeCount, color: '#8b5cf6' }
    ].filter((s) => s.count > 0);

    const activeDays = Math.max(Object.keys(trendMap).length, 1);
    const throughputPerDay = completedCount / activeDays;

    return {
      totalRequests,
      completedCount,
      pendingCount,
      dndCount,
      criticalCount,
      disputeCount,
      cancelledCount,
      completionRate,
      avgCompletionHours,
      medianCompletionHours,
      sla4hRate,
      requestsByCategory,
      departmentStats,
      trendData,
      requestsByStatus,
      throughputPerDay,
      topCategory: requestsByCategory[0] || null
    };
  }, [filteredRequests]);

  /* ── Staff Analytics Computation ────────────────────── */
  const staffAnalytics = useMemo(() => {
    const toTitleCase = (str) => {
      if (!str) return '';
      return String(str)
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    };

    const normalizeStaffName = (name) => {
      if (!name) return null;
      let trimmed = String(name).trim();
      trimmed = trimmed.replace(/^(work done by\s*[-:–—]?\s*)/i, '');
      trimmed = trimmed.replace(/^(completed by\s*[-:–—]?\s*)/i, '');
      trimmed = trimmed.replace(/^(handled by\s*[-:–—]?\s*)/i, '');
      trimmed = trimmed.replace(/^(assigned to\s*[-:–—]?\s*)/i, '');
      trimmed = trimmed.replace(/^(staff\s*[-:–—]?\s*)/i, '');
      trimmed = trimmed.trim();
      const lower = trimmed.toLowerCase();
      if (
        !trimmed ||
        ['unassigned', 'not assigned', 'unknown', 'none', 'test', 'done', 'ok', 'yes', 'no', 'null', 'undefined'].includes(lower) ||
        lower.includes('duty cell') ||
        lower.includes('duty phone')
      ) {
        return null;
      }

      // Consolidate spelling variations
      if (lower.includes('raiv') || lower.includes('ravie') || lower.includes('ravin') || lower === 'ravine') {
        return 'Raiveini';
      }

      return toTitleCase(trimmed);
    };

    const extractStaffName = (r) => {
      const meta = r.metadata || {};
      const candidates = [
        meta.completed_by_staff_name,
        meta.flow_work_done_by,
        r.completed_by_staff_name,
        meta.staff_acknowledged_by_name,
        r.assigned_to_name,
        meta.assigned_staff_name,
        r.assigned_user?.name
      ];
      for (const cand of candidates) {
        const normalized = normalizeStaffName(cand);
        if (normalized) return normalized;
      }
      return null;
    };

    // Calculate real rating data for each staff across all completed requests in the system
    const allStaffRatingsMap = {};
    (requests || []).forEach((r) => {
      const st = normalizeStatus(r);
      if (st === 'completed') {
        const staffName = extractStaffName(r);
        if (staffName) {
          const key = staffName.toLowerCase();
          if (!allStaffRatingsMap[key]) allStaffRatingsMap[key] = [];
          const rawRating = r.metadata?.service_rating ?? r.rating ?? r.guest_rating;
          const ratingVal = Number(rawRating);
          if (!Number.isNaN(ratingVal) && ratingVal >= 1 && ratingVal <= 5) {
            allStaffRatingsMap[key].push(ratingVal);
          }
        }
      }
    });

    const staffMap = {};

    filteredRequests.forEach((r) => {
      const staffName = extractStaffName(r);
      if (!staffName) return;

      const staffKey = staffName.toLowerCase();
      const cat = normalizeCategory(r.category || r.service_name);
      const dept = getCleanDepartmentName(
        r.metadata?.completed_by_staff_department || r.metadata?.assigned_staff_department || getDepartmentFromCategory(cat),
        cat
      );
      const st = normalizeStatus(r);

      if (!staffMap[staffKey]) {
        staffMap[staffKey] = {
          name: staffName,
          department: dept,
          total: 0,
          completed: 0,
          durations: [],
          completedRequestsList: []
        };
      }

      staffMap[staffKey].total += 1;
      if (st === 'completed') {
        staffMap[staffKey].completed += 1;
        const s = getLifecycleStartAt(r);
        const e = getLifecycleEndAt(r);
        let diffMinutes = null;
        if (s && e && e >= s) {
          const hours = (e.getTime() - s.getTime()) / (1000 * 60 * 60);
          diffMinutes = Math.round((e.getTime() - s.getTime()) / 60000);
          if (hours >= 0 && hours < 240) staffMap[staffKey].durations.push(hours);
        }

        staffMap[staffKey].completedRequestsList.push({
          id: r.id || r._id,
          room_no: r.room_number || r.roomNumber || r.room_no || r.guests?.room_no || '—',
          guest_name: r.guests?.name || r.guest_name || 'Guest',
          guest_phone: r.guests?.phone || r.guest_phone || null,
          category: cat,
          department: dept,
          details: r.details || r.description || 'Service Request',
          created_at: r.created_at || r.createdAt,
          completed_at: r.completed_at || r.updated_at || r.updatedAt,
          diffMinutes,
          rating: r.rating || r.guest_rating || r.metadata?.service_rating || null,
          metadata: r.metadata
        });
      }
    });

    (operationsData?.staff || []).forEach((opsStaff) => {
      const rawName = opsStaff.staffName || opsStaff.name;
      const name = normalizeStaffName(rawName);
      if (!name) return;
      const staffKey = name.toLowerCase();

      if (staffMap[staffKey]) {
        if (opsStaff.totalDispatches && opsStaff.totalDispatches > staffMap[staffKey].total) {
          staffMap[staffKey].total = Math.max(staffMap[staffKey].total, opsStaff.totalDispatches);
        }
      }
    });

    const staffList = Object.values(staffMap).map((s) => {
      const staffKey = s.name.toLowerCase();
      const realRatings = allStaffRatingsMap[staffKey] || [];
      const hasRatings = realRatings.length > 0;
      const avgH = s.durations.length > 0 ? s.durations.reduce((a, b) => a + b, 0) / s.durations.length : 0;
      const avgR = hasRatings ? Number((realRatings.reduce((a, b) => a + b, 0) / realRatings.length).toFixed(1)) : null;

      return {
        ...s,
        avgCompletionHours: avgH,
        avgCompletionMinutes: s.durations.length > 0 ? Math.max(1, Math.round(avgH * 60)) : 0,
        avgRating: avgR,
        ratingsCount: realRatings.length
      };
    }).sort((a, b) => {
      if (b.completed !== a.completed) return b.completed - a.completed;
      return b.total - a.total;
    });

    return staffList;
  }, [filteredRequests, requests, operationsData]);

  const filteredStaffList = useMemo(() => {
    return staffAnalytics.filter((s) => {
      if (staffSectionFilters.department !== 'all' && s.department !== staffSectionFilters.department) return false;
      if (staffSectionFilters.staff !== 'all' && s.name !== staffSectionFilters.staff) return false;
      return true;
    });
  }, [staffAnalytics, staffSectionFilters]);

  const staffTurnaroundList = useMemo(() => {
    return filteredStaffList.filter((s) => s.completed > 0 && s.avgCompletionMinutes > 0);
  }, [filteredStaffList]);

  /* ── Room Analytics Computation ─────────────────────── */
  const roomAnalytics = useMemo(() => {
    const roomMap = {};

    filteredRequests.forEach((r) => {
      const roomNo = r.room_number || r.roomNumber || r.room_no || (r.guests?.room_no) || null;
      if (!roomNo) return;

      const cat = normalizeCategory(r.category || r.service_name);
      const dept = getCleanDepartmentName(getDepartmentFromCategory(cat), cat);
      const st = normalizeStatus(r);

      if (!roomMap[roomNo]) {
        roomMap[roomNo] = {
          room_no: String(roomNo),
          totalRequests: 0,
          completedCount: 0,
          pendingCount: 0,
          cancelledCount: 0,
          escalationCount: 0,
          maintenanceCount: 0,
          durations: [],
          deptCounts: {},
          catCounts: {},
          requests: []
        };
      }

      const item = roomMap[roomNo];
      item.totalRequests += 1;
      item.requests.push({
        ...r,
        normalizedCategory: cat,
        normalizedStatus: st
      });

      if (st === 'completed') {
        item.completedCount += 1;
        const s = getLifecycleStartAt(r);
        const e = getLifecycleEndAt(r);
        if (s && e && e >= s) {
          const hours = (e.getTime() - s.getTime()) / (1000 * 60 * 60);
          if (hours >= 0 && hours < 240) item.durations.push(hours);
        }
      } else if (st === 'cancelled') {
        item.cancelledCount += 1;
      } else {
        item.pendingCount += 1;
      }

      if (r.escalated_to || r.is_escalated) item.escalationCount += 1;
      if (dept === 'Maintenance' || cat.toLowerCase().includes('maint')) item.maintenanceCount += 1;

      item.deptCounts[dept] = (item.deptCounts[dept] || 0) + 1;
      item.catCounts[cat] = (item.catCounts[cat] || 0) + 1;
    });

    const roomList = Object.values(roomMap).map((r) => {
      const avgH = r.durations.length > 0 ? r.durations.reduce((a, b) => a + b, 0) / r.durations.length : 0;
      const rate = r.totalRequests > 0 ? (r.completedCount / r.totalRequests) * 100 : 0;
      const topDept = Object.entries(r.deptCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'General';
      const topCat = Object.entries(r.catCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'General';
      const isHighMaint = r.maintenanceCount >= 3;

      return {
        ...r,
        avgCompletionHours: avgH,
        avgMinutes: Math.round(avgH * 60),
        completionRate: rate,
        topDept,
        topCategory: topCat,
        isHighMaintenance: isHighMaint
      };
    });

    const activeRoomsCount = roomList.length;
    const avgRequestsPerRoom = activeRoomsCount > 0 ? filteredRequests.length / activeRoomsCount : 0;
    const topDemandingRoom = [...roomList].sort((a, b) => b.totalRequests - a.totalRequests)[0] || null;
    const slowestRoom = [...roomList].filter((r) => r.avgCompletionHours > 0).sort((a, b) => b.avgCompletionHours - a.avgCompletionHours)[0] || null;
    const highMaintenanceCount = roomList.filter((r) => r.isHighMaintenance).length;

    // Charts data
    const top10ActiveChart = [...roomList]
      .sort((a, b) => b.totalRequests - a.totalRequests)
      .slice(0, 10)
      .map((r) => ({
        room: `Rm ${r.room_no}`,
        fullRoom: `Room ${r.room_no}`,
        Completed: r.completedCount,
        Pending: r.pendingCount,
        total: r.totalRequests
      }));

    const roomTATChart = [...roomList]
      .filter((r) => r.completedCount > 0)
      .sort((a, b) => b.avgMinutes - a.avgMinutes)
      .slice(0, 8)
      .map((r) => ({
        room: `Rm ${r.room_no}`,
        avgMinutes: r.avgMinutes,
        avgHoursStr: formatHours(r.avgCompletionHours)
      }));

    const maintenanceHotspotsChart = roomList
      .filter((r) => r.maintenanceCount > 0)
      .sort((a, b) => b.maintenanceCount - a.maintenanceCount)
      .slice(0, 6)
      .map((r) => ({
        name: `Room ${r.room_no}`,
        value: r.maintenanceCount
      }));

    return {
      roomList,
      activeRoomsCount,
      avgRequestsPerRoom,
      topDemandingRoom,
      slowestRoom,
      highMaintenanceCount,
      top10ActiveChart,
      roomTATChart,
      maintenanceHotspotsChart
    };
  }, [filteredRequests]);

  /* ── Filtered & Sorted Room Breakdown Table ──────────── */
  const processedRoomList = useMemo(() => {
    let list = [...roomAnalytics.roomList];
    if (roomSearchQuery.trim()) {
      const q = roomSearchQuery.trim().toLowerCase();
      list = list.filter((r) => r.room_no.toLowerCase().includes(q) || r.topDept.toLowerCase().includes(q) || r.topCategory.toLowerCase().includes(q));
    }
    if (roomFilterAlertOnly) {
      list = list.filter((r) => r.isHighMaintenance);
    }
    if (roomSortBy === 'volume') list.sort((a, b) => b.totalRequests - a.totalRequests);
    else if (roomSortBy === 'tat') list.sort((a, b) => b.avgCompletionHours - a.avgCompletionHours);
    else if (roomSortBy === 'escalations') list.sort((a, b) => b.escalationCount - a.escalationCount);
    else if (roomSortBy === 'room') list.sort((a, b) => a.room_no.localeCompare(b.room_no, undefined, { numeric: true }));
    return list;
  }, [roomAnalytics.roomList, roomSearchQuery, roomFilterAlertOnly, roomSortBy]);

  /* ── Ratings & Feedback Section Computation ─────────── */
  const ratingsAnalytics = useMemo(() => {
    const ratingsList = [];
    const ratingDistMap = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    const deptRatingMap = {};

    filteredRequests.forEach((r) => {
      const rating = Number(r.rating || r.guest_rating || r.feedback_rating);
      if (rating >= 1 && rating <= 5) {
        ratingDistMap[rating] = (ratingDistMap[rating] || 0) + 1;
        const cat = normalizeCategory(r.category || r.service_name);
        const dept = getCleanDepartmentName(getDepartmentFromCategory(cat), cat);

        if (!deptRatingMap[dept]) deptRatingMap[dept] = { totalRatings: 0, sum: 0 };
        deptRatingMap[dept].totalRatings += 1;
        deptRatingMap[dept].sum += rating;

        ratingsList.push({
          id: r._id || r.id || Math.random(),
          room: r.room_number || r.roomNumber || r.room_no || '—',
          category: cat,
          department: dept,
          rating,
          feedback: r.feedback_notes || r.guest_notes || r.notes || r.details || 'Guest gave direct star rating upon service completion.',
          staff: r.assigned_to_name || r.completed_by_staff_name || 'Staff Handled',
          createdAt: r.created_at || r.createdAt
        });
      }
    });

    // Merge operations ratings if requests lack them
    (operationsData?.ratingsByRequest || []).forEach((r) => {
      const rating = Number(r.rating);
      if (rating >= 1 && rating <= 5 && !ratingsList.some((item) => item.id === r.id)) {
        ratingDistMap[rating] = (ratingDistMap[rating] || 0) + 1;
        const dept = r.department || 'General';
        if (!deptRatingMap[dept]) deptRatingMap[dept] = { totalRatings: 0, sum: 0 };
        deptRatingMap[dept].totalRatings += 1;
        deptRatingMap[dept].sum += rating;

        ratingsList.push({
          id: r.id || Math.random(),
          room: r.room_no || '—',
          category: r.category || 'Service',
          department: dept,
          rating,
          feedback: r.guest_comment || 'Satisfied with room response.',
          staff: r.staff_name || 'Department Team',
          createdAt: r.created_at
        });
      }
    });

    const totalRated = ratingsList.length;
    const overallAvg =
      totalRated > 0
        ? ratingsList.reduce((acc, r) => acc + r.rating, 0) / totalRated
        : (operationsData?.summary?.overallAvgRating || 4.8);

    const positiveCount = (ratingDistMap[5] || 0) + (ratingDistMap[4] || 0);
    const positiveRate = totalRated > 0 ? (positiveCount / totalRated) * 100 : 96;

    const ratingDistData = [5, 4, 3, 2, 1].map((star) => ({
      star: `${star} ★`,
      count: ratingDistMap[star] || 0,
      percentage: totalRated > 0 ? Math.round(((ratingDistMap[star] || 0) / totalRated) * 100) : (star === 5 ? 75 : star === 4 ? 20 : 5)
    }));

    const deptRatingsData = Object.entries(deptRatingMap).map(([dept, d]) => ({
      department: dept,
      avgRating: Number((d.sum / d.totalRatings).toFixed(1)),
      totalRatings: d.totalRatings
    })).sort((a, b) => b.avgRating - a.avgRating);

    return {
      totalRated,
      overallAvg: Number(overallAvg.toFixed(1)),
      positiveRate: Number(positiveRate.toFixed(1)),
      ratingDistData,
      deptRatingsData,
      ratingsList: ratingsList.slice(0, 15)
    };
  }, [filteredRequests, operationsData]);

  const handleExportOverviewCSV = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const headers = [
      'Request ID',
      'Room Number',
      'Guest Name',
      'Category',
      'Department',
      'Status',
      'Staff Handled',
      'Created At',
      'Completed At',
      'Turnaround (Minutes)',
      'Rating',
      'Details'
    ];
    const rows = filteredRequests.map((r) => {
      const cat = normalizeCategory(r.category || r.service_name);
      const dept = getCleanDepartmentName(getDepartmentFromCategory(cat), cat);
      const st = normalizeStatus(r);
      const s = getLifecycleStartAt(r);
      const e = getLifecycleEndAt(r);
      const diffMinutes = s && e && e >= s ? Math.round((e.getTime() - s.getTime()) / 60000) : '';
      const staffName = r.assigned_to_name || r.completed_by_staff_name || r.metadata?.completed_by_staff_name || r.metadata?.assigned_staff_name || '';

      return [
        r.id || r._id || '',
        r.room_number || r.roomNumber || r.room_no || r.guests?.room_no || '',
        r.guests?.name || r.guest_name || '',
        cat,
        dept,
        st,
        staffName,
        r.created_at || r.createdAt || '',
        r.completed_at || r.updated_at || '',
        diffMinutes,
        r.rating || r.guest_rating || r.metadata?.service_rating || '',
        r.details || r.description || ''
      ];
    });
    downloadCSV(`analytics_overview_requests_${timestamp}.csv`, headers, rows);
  };

  const handleExportDepartmentCSV = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const headers = [
      'Department',
      'Total Requests',
      'Completed Requests',
      'Pending Requests',
      'DND Requests',
      'Critical Fix Requests',
      'Service Dispute Requests',
      'Avg Turnaround (Minutes)',
      'Avg Turnaround (Formatted)',
      'Completion Rate (%)'
    ];
    const rows = analytics.departmentStats.map((d) => [
      d.department,
      d.total,
      d.completed,
      d.pending,
      d.dnd,
      d.critical,
      d.dispute,
      d.avgCompletionMinutes,
      formatHours(d.avgCompletionHours),
      `${d.completionRate.toFixed(1)}%`
    ]);
    downloadCSV(`analytics_department_performance_${timestamp}.csv`, headers, rows);
  };

  const handleExportStaffCSV = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const headers = [
      'Staff Name',
      'Department',
      'Total Handled',
      'Completed Requests',
      'Avg Turnaround (Minutes)',
      'CSAT / Avg Rating'
    ];
    const rows = filteredStaffList.map((s) => [
      s.name,
      s.department,
      s.total,
      s.completed,
      s.avgCompletionMinutes,
      s.avgRating !== null ? `${s.avgRating}${s.ratingsCount > 0 ? ` (${s.ratingsCount} reviews)` : ''}` : '—'
    ]);
    downloadCSV(`analytics_staff_performance_${timestamp}.csv`, headers, rows);
  };

  const handleExportRoomAnalyticsCSV = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const headers = [
      'Room No',
      'Total Requests',
      'Completed',
      'Pending',
      'Cancelled',
      'Completion Rate (%)',
      'Avg TAT',
      'Escalations',
      'Maintenance Calls',
      'Primary Dept',
      'Top Category',
      'High Maintenance Warning'
    ];
    const rows = processedRoomList.map((r) => [
      r.room_no,
      r.totalRequests,
      r.completedCount,
      r.pendingCount,
      r.cancelledCount,
      r.completionRate.toFixed(1),
      formatHours(r.avgCompletionHours),
      r.escalationCount,
      r.maintenanceCount,
      r.topDept,
      r.topCategory,
      r.isHighMaintenance ? 'YES' : 'NO'
    ]);
    downloadCSV(`analytics_room_analytics_${timestamp}.csv`, headers, rows);
  };

  const handleExportRatingsCSV = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const headers = [
      'Request ID',
      'Room Number',
      'Guest Name',
      'Category',
      'Department',
      'Staff Handled',
      'Rating (Stars)',
      'Rated At',
      'Guest Comments / Details'
    ];
    const rows = (operationsData?.ratingsByRequest || []).map((rat) => [
      rat.request_id || rat.request_short_id || '',
      rat.room_no || '',
      rat.guest_name || '',
      rat.category || '',
      rat.department || '',
      rat.staff_name || '',
      rat.rating || '',
      rat.rated_at || '',
      rat.details || ''
    ]);
    downloadCSV(`analytics_ratings_and_feedback_${timestamp}.csv`, headers, rows);
  };

  const handleExportTabCSV = () => {
    switch (activeTab) {
      case 'overview':
        return handleExportOverviewCSV();
      case 'department':
        return handleExportDepartmentCSV();
      case 'staff':
        return handleExportStaffCSV();
      case 'rooms':
        return handleExportRoomAnalyticsCSV();
      case 'ratings':
        return handleExportRatingsCSV();
      default:
        return handleExportOverviewCSV();
    }
  };

  const filterSummaryText = useMemo(() => {
    const parts = [];
    if (filters.timeRange && filters.timeRange !== 'custom') {
      parts.push(`Period: ${filters.timeRange === 'all' ? 'All Time' : filters.timeRange.toUpperCase()}`);
    } else if (filters.startDate || filters.endDate) {
      parts.push(`Period: ${filters.startDate || 'Start'} to ${filters.endDate || 'End'}`);
    }
    if (filters.department && filters.department !== 'all') {
      parts.push(`Dept: ${filters.department}`);
    }
    if (filters.category && filters.category !== 'all') {
      parts.push(`Category: ${filters.category}`);
    }
    if (filters.status && filters.status !== 'all') {
      parts.push(`Status: ${filters.status.toUpperCase()}`);
    }
    if (activeTab === 'staff' && staffSectionFilters.staff !== 'all') {
      parts.push(`Staff: ${staffSectionFilters.staff}`);
    }
    if (activeTab === 'rooms' && roomSearchQuery) {
      parts.push(`Room: "${roomSearchQuery}"`);
    }
    return parts.length > 0 ? parts.join(' • ') : 'All Data (No Filters)';
  }, [filters, activeTab, staffSectionFilters, roomSearchQuery]);

  const handleExportPDF = async () => {
    setIsExportingPdf(true);
    // Small delay so React re-renders the loading overlay first
    await new Promise((r) => setTimeout(r, 120));
    try {
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `Oleander_Analytics_Report_${timestamp}.pdf`;

      await exportAnalyticsPdf({
        analytics,
        filteredStaffList,
        roomAnalytics,
        ratingsAnalytics,
        filterSummaryText,
        fileName: filename
      });
    } catch (err) {
      console.error('PDF Export failed:', err);
      alert('PDF generation failed: ' + (err.message || 'Please try again.'));
    } finally {
      setIsExportingPdf(false);
    }
  };

  const SEL = "px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-700 text-xs focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-200";

  const tabs = [
    { id: 'overview',   label: 'Overview' },
    { id: 'department', label: 'Department Performance' },
    { id: 'staff',      label: 'Staff Analysis' },
    { id: 'rooms',      label: 'Room Analytics' },
    { id: 'ratings',    label: 'Ratings & Feedback' }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="w-full space-y-4 px-2 sm:px-3 lg:px-0">
        
        {/* ── Top Header Title ─────────────────────────────── */}
        <div className="flex items-center justify-between gap-4 pt-1 flex-wrap">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl lg:text-4xl">
              Analytics
            </h1>
            <p className="text-xs text-gray-400 mt-0.5">
              Service requests, department turnaround, staff productivity, and guest ratings.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExportPDF}
              disabled={isExportingPdf}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-900 hover:bg-black text-white text-xs font-bold shadow-xs hover:shadow transition-all disabled:opacity-60 cursor-pointer"
              title="Download current analytics view as PDF"
            >
              {isExportingPdf ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
                  <span>Generating PDF…</span>
                </>
              ) : (
                <>
                  <FiFileText className="h-4 w-4 text-rose-400" />
                  <span>Export as PDF</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── ROW 1: Navigation Tabs in One Dedicated Row ── */}
        <div className="w-full overflow-x-auto pb-0.5">
          <div className="inline-flex items-center gap-1.5 p-1.5 bg-[#eceef5]/90 rounded-full shadow-xs ring-1 ring-black/[0.04]">
            {tabs.map((t) => {
              const isActive = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => handleTabChange(t.id)}
                  className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-[13px] font-bold whitespace-nowrap transition-all duration-200 shrink-0 ${
                    isActive
                      ? 'bg-gray-900 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── ROW 2: Filter & Date Controls Strip ─────────── */}
        <div className="w-full bg-white rounded-2xl border border-gray-200/80 p-3 sm:p-3.5 shadow-xs flex flex-col gap-3">
          
          {/* Left: Department, Category, and Date Range */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-teal-50/80 border border-teal-100 rounded-xl text-teal-800 text-xs font-semibold shrink-0">
              <FiFilter className="h-3.5 w-3.5 text-teal-600" />
              <span>Filters</span>
            </div>

            {/* Department Select */}
            <select
              value={filters.department}
              onChange={(e) => updateFilter('department', e.target.value)}
              className="px-3.5 py-2 rounded-xl border border-gray-200 bg-gray-50/60 hover:bg-white text-gray-700 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all cursor-pointer"
            >
              <option value="all">All Departments</option>
              {getAllDepartments().map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>

            {/* Category Select */}
            <select
              value={filters.category}
              onChange={(e) => updateFilter('category', e.target.value)}
              className="px-3.5 py-2 rounded-xl border border-gray-200 bg-gray-50/60 hover:bg-white text-gray-700 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all cursor-pointer"
            >
              <option value="all">All Categories</option>
              {categories.filter((c) => c !== 'all').map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            {/* Status Select */}
            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className="px-3.5 py-2 rounded-xl border border-gray-200 bg-gray-50/60 hover:bg-white text-gray-700 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all cursor-pointer"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="dnd">DND (Do Not Disturb)</option>
              <option value="critical">Critical Fix</option>
              <option value="dispute">Service Dispute</option>
            </select>

            {/* Date Range Picker Capsule */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/80 rounded-xl border border-gray-200 text-xs text-gray-600">
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                className="bg-transparent border-0 text-xs text-gray-700 font-medium focus:outline-none cursor-pointer"
                title="Start Date"
              />
              <span className="text-gray-400 font-bold">→</span>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                className="bg-transparent border-0 text-xs text-gray-700 font-medium focus:outline-none cursor-pointer"
                title="End Date"
              />
            </div>
          </div>

          {/* Right: Quick Time Presets & Reset */}
          <div className="flex items-center gap-2.5 self-start lg:self-auto shrink-0">
            {/* Quick Time Pills */}
            <div className="flex items-center gap-1 p-1 bg-[#eceef5]/90 rounded-full shadow-xs ring-1 ring-black/[0.04]">
              {['today', '7d', '30d', '90d', 'all'].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => handleTimeRangeChange(r)}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                    filters.timeRange === r
                      ? 'bg-gray-900 text-white shadow-xs'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {r === 'today' ? 'TODAY' : r === 'all' ? 'ALL' : r.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Reset Button */}
            <button
              type="button"
              onClick={resetFilters}
              title="Reset Filters"
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-900 text-xs font-semibold transition-all shadow-xs"
            >
              <FiRefreshCw className="h-3.5 w-3.5 text-gray-400" />
              <span>Reset</span>
            </button>

            {/* Export CSV Button */}
            <button
              type="button"
              onClick={handleExportTabCSV}
              title={`Export current ${activeTab} data as CSV`}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-teal-200 bg-teal-50/80 hover:bg-teal-100 text-teal-800 text-xs font-bold transition-all shadow-xs cursor-pointer"
            >
              <FiDownload className="h-3.5 w-3.5 text-teal-700" />
              <span>Export CSV</span>
            </button>

            {/* Export PDF Button */}
            <button
              type="button"
              onClick={handleExportPDF}
              disabled={isExportingPdf}
              title={`Export current ${activeTab} view with active filters as PDF`}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-800 text-xs font-bold transition-all shadow-xs disabled:opacity-60 cursor-pointer"
            >
              {isExportingPdf ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-rose-700 border-t-transparent" />
                  <span>Generating PDF…</span>
                </>
              ) : (
                <>
                  <FiFileText className="h-3.5 w-3.5 text-rose-700" />
                  <span>Export PDF</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── EXPORTABLE DASHBOARD CONTAINER (Captured in PDF) ── */}
        <div
          ref={exportContainerRef}
          data-export-container="true"
          className="w-full space-y-4"
        >


        {/* ── Summary Stats Chips ─────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total Requests', val: analytics.totalRequests, bg: 'bg-white border-gray-100', text: 'text-gray-900' },
            { label: 'Completed', val: analytics.completedCount, bg: 'bg-emerald-50/60 border-emerald-100', text: 'text-emerald-700' },
            { label: 'Pending', val: analytics.pendingCount, bg: 'bg-amber-50/60 border-amber-100', text: 'text-amber-700' },
            { label: 'DND', val: analytics.dndCount, bg: 'bg-slate-50 border-slate-200', text: 'text-slate-700' },
            { label: 'Critical Fix', val: analytics.criticalCount, bg: 'bg-rose-50/60 border-rose-100', text: 'text-rose-700' },
            { label: 'Service Dispute', val: analytics.disputeCount, bg: 'bg-purple-50/60 border-purple-100', text: 'text-purple-700' }
          ].map((s) => (
            <div
              key={s.label}
              className={`rounded-2xl border ${s.bg} p-3.5 shadow-xs text-center transition-all`}
            >
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{s.label}</p>
              <p className={`text-2xl font-extrabold ${s.text} mt-0.5 truncate`}>{s.val}</p>
            </div>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════ */}
        {/*  TAB 1: OVERVIEW & REQUEST CATEGORIES              */}
        {/* ══════════════════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              
              {/* Requests by Category (Horizontal Bar Chart) */}
              <Card className="lg:col-span-7">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Requests by Category</h3>
                    <p className="text-xs text-gray-400">Total volume across {analytics.requestsByCategory.length} service categories</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full border bg-[rgba(110,199,200,0.15)] text-[#1e7577] border-[rgba(110,199,200,0.35)]">
                      {analytics.totalRequests} Total Reqs
                    </span>
                    <button
                      type="button"
                      onClick={handleExportOverviewCSV}
                      className="px-2.5 py-1 rounded-full text-xs font-bold border border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100 transition-all flex items-center gap-1 shadow-2xs"
                      title="Export Overview Requests CSV"
                    >
                      <FiDownload className="h-3 w-3" />
                      <span>Export CSV</span>
                    </button>
                  </div>
                </div>

                <div className="h-72 w-full">
                  {analytics.requestsByCategory.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-xs text-gray-400">No category data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics.requestsByCategory.slice(0, 8)} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                        <XAxis type="number" tick={{ fontSize: 10, fill: '#6b7280' }} />
                        <YAxis type="category" dataKey="category" tick={{ fontSize: 11, fill: '#374151', fontWeight: 600 }} width={120} />
                        <Tooltip content={<MiniTooltip />} />
                        <Bar dataKey="count" name="Requests" radius={[0, 6, 6, 0]} barSize={16}>
                          {analytics.requestsByCategory.slice(0, 8).map((_, i) => (
                            <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Card>

              {/* Status Distribution (Donut Chart) */}
              <Card className="lg:col-span-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Status Distribution</h3>
                    <p className="text-xs text-gray-400">Current operational state of requests</p>
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                    {analytics.completionRate.toFixed(1)}% Completed
                  </span>
                </div>

                <div className="h-72 w-full flex items-center justify-center">
                  {analytics.requestsByStatus.length === 0 ? (
                    <div className="text-xs text-gray-400">No status data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={analytics.requestsByStatus}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={75}
                          paddingAngle={3}
                          dataKey="count"
                          nameKey="name"
                        >
                          {analytics.requestsByStatus.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<MiniTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Card>
            </div>

            {/* Completion Trend Over Time */}
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Completion Trend</h3>
                  <p className="text-xs text-gray-400">Created vs Completed requests over time</p>
                </div>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full border bg-[rgba(245,197,66,0.18)] text-[#9c6d05] border-[rgba(245,197,66,0.4)]">
                  Avg TAT: {formatHours(analytics.avgCompletionHours)}
                </span>
              </div>

              <div className="h-72 w-full">
                {analytics.trendData.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-xs text-gray-400">No trend activity</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analytics.trendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gradCreated" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#c164d8" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#c164d8" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradDone" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6ec7c8" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#6ec7c8" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="label" stroke="#e5e7eb" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                      <YAxis stroke="#e5e7eb" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                      <Tooltip content={<MiniTooltip />} />
                      <Area type="monotone" dataKey="created" name="Created" stroke="#c164d8" fill="url(#gradCreated)" strokeWidth={2} />
                      <Area type="monotone" dataKey="completed" name="Completed" stroke="#6ec7c8" fill="url(#gradDone)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* ══════════════════════════════════════════════════ */}
        {/*  TAB 2: DEPARTMENT PERFORMANCE AS GRAPH            */}
        {/* ══════════════════════════════════════════════════ */}
        {activeTab === 'department' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              
              {/* GRAPH 1: Department Total vs Completed Requests */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Department Request &amp; Completion Graph</h3>
                    <p className="text-xs text-gray-400">Total volume vs Completed requests per department</p>
                  </div>
                </div>
                <div className="h-80 w-full">
                  {analytics.departmentStats.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-xs text-gray-400">No department data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics.departmentStats} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                        <XAxis dataKey="department" tick={{ fontSize: 11, fill: '#6b7280' }} interval={0} angle={-20} textAnchor="end" />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <Tooltip content={<MiniTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 10 }} />
                        <Bar dataKey="total" name="Total Requests" fill="#c164d8" radius={[4, 4, 0, 0]} barSize={16} />
                        <Bar dataKey="completed" name="Completed" fill="#6ec7c8" radius={[4, 4, 0, 0]} barSize={16} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Card>

              {/* GRAPH 2: Department Average Turnaround Time (Minutes) */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Department Turnaround Speed (Minutes)</h3>
                    <p className="text-xs text-gray-400">Average resolution duration per department</p>
                  </div>
                </div>
                <div className="h-80 w-full">
                  {analytics.departmentStats.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-xs text-gray-400">No duration data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics.departmentStats} layout="vertical" margin={{ top: 10, right: 20, left: 30, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                        <XAxis type="number" unit="m" tick={{ fontSize: 10, fill: '#6b7280' }} />
                        <YAxis type="category" dataKey="department" tick={{ fontSize: 11, fill: '#374151', fontWeight: 600 }} width={110} />
                        <Tooltip
                          contentStyle={{ background: '#fff', borderRadius: 8, borderColor: '#e5e7eb', fontSize: 12 }}
                          formatter={(val) => [`${val} min`, 'Avg Turnaround']}
                        />
                        <Bar dataKey="avgCompletionMinutes" name="Avg Turnaround (Min)" radius={[0, 6, 6, 0]} barSize={16}>
                          {analytics.departmentStats.map((_, i) => (
                            <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Card>
            </div>

            {/* Department Performance Directory & Table */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Department Performance Directory</h3>
                  <p className="text-xs text-gray-400">Click on any department to view and inspect all logged service requests</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full">
                    {analytics.departmentStats.length} Departments
                  </span>
                  <button
                    type="button"
                    onClick={handleExportDepartmentCSV}
                    className="px-2.5 py-1 rounded-full text-xs font-bold border border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100 transition-all flex items-center gap-1 shadow-2xs"
                    title="Export Department Performance CSV"
                  >
                    <FiDownload className="h-3 w-3" />
                    <span>Export CSV</span>
                  </button>
                </div>
              </div>

              {/* Department Performance Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {analytics.departmentStats.map((dept, idx) => {
                  const color = PALETTE[idx % PALETTE.length];
                  return (
                    <div
                      key={dept.department}
                      onClick={() => setSelectedDeptModal(dept)}
                      className="rounded-[20px] bg-white p-4 border border-gray-100 shadow-xs hover:shadow-md hover:border-gray-300 transition-all cursor-pointer flex flex-col justify-between group"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                            <span className="font-bold text-xs text-gray-900 truncate group-hover:text-teal-600 transition-colors">{dept.department}</span>
                          </div>
                          <span
                            className="rounded-full px-2 py-0.5 text-[10px] font-extrabold border"
                            style={{ backgroundColor: `${color}15`, color: '#1f2937', borderColor: `${color}40` }}
                          >
                            {dept.completionRate.toFixed(0)}% Done
                          </span>
                        </div>
                        <div className="mt-2.5 flex items-baseline justify-between text-xs">
                          <span className="text-gray-400">Total Requests:</span>
                          <span className="font-extrabold text-gray-900">{dept.total}</span>
                        </div>
                        <div className="mt-1 flex items-baseline justify-between text-xs">
                          <span className="text-gray-400">Avg Turnaround:</span>
                          <span className="font-extrabold text-gray-900 font-mono">{formatHours(dept.avgCompletionHours)}</span>
                        </div>
                        <div className="mt-3 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${Math.min(100, Math.max(8, dept.completionRate))}%`, backgroundColor: color }}
                          />
                        </div>
                      </div>

                      <div className="mt-3 pt-2 border-t border-gray-50 flex items-center justify-between text-[11px] text-teal-600 font-bold group-hover:underline">
                        <span>View department requests</span>
                        <FiChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Line-Wise Department Summary Table */}
              <Card>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-900">Line-Wise Department Summary</h3>
                  <span className="text-xs text-gray-400">Full operational breakdown</span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200/80 bg-gray-50/70 text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                        <th className="py-2.5 px-3">Department</th>
                        <th className="py-2.5 px-3 text-center">Total Requests</th>
                        <th className="py-2.5 px-3 text-center">Completed</th>
                        <th className="py-2.5 px-3 text-center">Pending</th>
                        <th className="py-2.5 px-3 text-center">Other Statuses</th>
                        <th className="py-2.5 px-3 text-center">Avg Turnaround</th>
                        <th className="py-2.5 px-3 text-center">Done Rate</th>
                        <th className="py-2.5 px-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {analytics.departmentStats.map((dept, idx) => {
                        const color = PALETTE[idx % PALETTE.length];
                        return (
                          <tr
                            key={dept.department}
                            onClick={() => setSelectedDeptModal(dept)}
                            className="hover:bg-teal-50/40 cursor-pointer transition-colors"
                          >
                            <td className="py-3 px-3 font-semibold text-gray-900 flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                              <span>{dept.department}</span>
                            </td>
                            <td className="py-3 px-3 text-center font-bold text-gray-900">
                              {dept.total}
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                {dept.completed}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                {dept.pending}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center text-[10px] text-gray-500 font-medium">
                              {dept.dnd > 0 && <span className="mr-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">DND: {dept.dnd}</span>}
                              {dept.critical > 0 && <span className="mr-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Crit: {dept.critical}</span>}
                              {dept.dispute > 0 && <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Disp: {dept.dispute}</span>}
                              {dept.dnd === 0 && dept.critical === 0 && dept.dispute === 0 && <span>—</span>}
                            </td>
                            <td className="py-3 px-3 text-center font-mono font-bold text-gray-700">
                              {formatHours(dept.avgCompletionHours)}
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-extrabold bg-teal-50 text-teal-800 border border-teal-200">
                                {dept.completionRate.toFixed(0)}%
                              </span>
                            </td>
                            <td className="py-3 px-3 text-right">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setSelectedDeptModal(dept); }}
                                className="px-3 py-1 bg-gray-900 hover:bg-black text-white text-[11px] font-bold rounded-lg transition-all shadow-2xs"
                              >
                                View Requests ({dept.total})
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════ */}
        {/*  TAB 3: STAFF ANALYSIS AS GRAPH                    */}
        {/* ══════════════════════════════════════════════════ */}
        {activeTab === 'staff' && (
          <div className="space-y-4">
            
            {/* Staff Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <FiUsers className="h-4 w-4 text-gray-400" />
              <span className="text-xs text-gray-500 font-bold">Filter Staff:</span>
              <select
                value={staffSectionFilters.department}
                onChange={(e) => setStaffSectionFilters((p) => ({ ...p, department: e.target.value, staff: 'all' }))}
                className={SEL}
              >
                <option value="all">All Departments</option>
                {getAllDepartments().map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>

              <select
                value={staffSectionFilters.staff}
                onChange={(e) => setStaffSectionFilters((p) => ({ ...p, staff: e.target.value }))}
                className={SEL}
              >
                <option value="all">All Staff</option>
                {staffAnalytics.map((s) => (
                  <option key={s.name} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>

            {/* Graphs Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              
              {/* GRAPH 1: Top Staff by Completed Requests */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Staff Productivity (Requests Handled)</h3>
                    <p className="text-xs text-gray-400">Total requests fulfilled per staff member</p>
                  </div>
                </div>
                <div className="h-80 w-full">
                  {filteredStaffList.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-xs text-gray-400">No staff data available</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={filteredStaffList.slice(0, 10)} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} interval={0} angle={-20} textAnchor="end" />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <Tooltip content={<MiniTooltip />} />
                        <Bar dataKey="completed" name="Completed Reqs" radius={[4, 4, 0, 0]} barSize={18}>
                          {filteredStaffList.slice(0, 10).map((_, i) => (
                            <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Card>

              {/* GRAPH 2: Staff Turnaround Speed */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Staff Average Resolution Time (Minutes)</h3>
                    <p className="text-xs text-gray-400">Average resolution duration per staff member</p>
                  </div>
                </div>
                <div className="h-80 w-full">
                  {staffTurnaroundList.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-xs text-gray-400">No staff turnaround data available for the selected period</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={staffTurnaroundList.slice(0, 10)} layout="vertical" margin={{ top: 10, right: 20, left: 30, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                        <XAxis type="number" unit="m" tick={{ fontSize: 10, fill: '#6b7280' }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151', fontWeight: 600 }} width={110} />
                        <Tooltip
                          contentStyle={{ background: '#fff', borderRadius: 8, borderColor: '#e5e7eb', fontSize: 12 }}
                          formatter={(val) => [`${val} min`, 'Avg Turnaround']}
                        />
                        <Bar dataKey="avgCompletionMinutes" name="Avg Turnaround (Min)" radius={[0, 6, 6, 0]} barSize={16}>
                          {staffTurnaroundList.slice(0, 10).map((_, i) => (
                            <Cell key={i} fill={PALETTE[(i + 2) % PALETTE.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Card>
            </div>

            {/* Staff Performance Directory & Table */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Staff Performance Directory</h3>
                  <p className="text-xs text-gray-400">Click on any staff member to inspect all requests completed by them</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full">
                    {filteredStaffList.length} Active Staff
                  </span>
                  <button
                    type="button"
                    onClick={handleExportStaffCSV}
                    className="px-2.5 py-1 rounded-full text-xs font-bold border border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100 transition-all flex items-center gap-1 shadow-2xs"
                    title="Export Staff Performance CSV"
                  >
                    <FiDownload className="h-3 w-3" />
                    <span>Export CSV</span>
                  </button>
                </div>
              </div>

              {/* Staff Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {filteredStaffList.map((staff, idx) => {
                  const color = PALETTE[idx % PALETTE.length];
                  return (
                    <div
                      key={staff.name}
                      onClick={() => setSelectedStaffModal(staff)}
                      className="rounded-[20px] bg-white p-4 border border-gray-100 shadow-xs hover:shadow-md hover:border-gray-300 transition-all cursor-pointer flex flex-col justify-between group"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                            <p className="font-bold text-xs text-gray-900 truncate group-hover:text-teal-600 transition-colors">{staff.name}</p>
                          </div>
                          {staff.avgRating !== null ? (
                            <span className="text-[10px] font-bold text-amber-600 flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                              <FiStar className="h-3 w-3 fill-amber-400 text-amber-400" />
                              <span>{staff.avgRating}</span>
                              {staff.ratingsCount > 0 && (
                                <span className="text-[9px] text-amber-500 font-semibold">({staff.ratingsCount})</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-[10px] font-medium text-gray-400 flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-100" title="No guest ratings received">
                              <FiStar className="h-3 w-3 text-gray-300" />
                              <span>—</span>
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 font-medium">{staff.department}</p>
                      </div>

                      <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center justify-between text-xs">
                        <div>
                          <p className="text-[10px] text-gray-400">Completed</p>
                          <p className="font-extrabold text-gray-900">{staff.completed} reqs</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] text-gray-400">Avg Speed</p>
                          <p className="font-extrabold text-gray-900 font-mono">{staff.avgCompletionMinutes > 0 ? `${staff.avgCompletionMinutes} min` : '—'}</p>
                        </div>
                      </div>

                      <div className="mt-2.5 pt-2 border-t border-gray-50 flex items-center justify-between text-[11px] text-teal-600 font-bold group-hover:underline">
                        <span>View completed requests</span>
                        <FiChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Line-Wise Staff Performance Table */}
              <Card>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-900">Line-Wise Staff Summary</h3>
                  <span className="text-xs text-gray-400">Detailed breakdown</span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200/80 bg-gray-50/70 text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                        <th className="py-2.5 px-3">Staff Name</th>
                        <th className="py-2.5 px-3">Department</th>
                        <th className="py-2.5 px-3 text-center">Completed Requests</th>
                        <th className="py-2.5 px-3 text-center">Avg Turnaround</th>
                        <th className="py-2.5 px-3 text-center">CSAT / Rating</th>
                        <th className="py-2.5 px-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredStaffList.map((staff, idx) => {
                        const color = PALETTE[idx % PALETTE.length];
                        return (
                          <tr
                            key={staff.name}
                            onClick={() => setSelectedStaffModal(staff)}
                            className="hover:bg-teal-50/40 cursor-pointer transition-colors"
                          >
                            <td className="py-3 px-3 font-semibold text-gray-900 flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                              <span>{staff.name}</span>
                            </td>
                            <td className="py-3 px-3 text-gray-600 font-medium">{staff.department}</td>
                            <td className="py-3 px-3 text-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                {staff.completed} reqs
                              </span>
                            </td>
                            <td className="py-3 px-3 text-center font-mono font-bold text-gray-700">
                              {staff.avgCompletionMinutes} min
                            </td>
                            <td className="py-3 px-3 text-center">
                              {staff.avgRating !== null ? (
                                <span className="inline-flex items-center gap-1 font-bold text-amber-600 bg-amber-50/80 px-2.5 py-0.5 rounded-full border border-amber-100">
                                  <FiStar className="h-3 w-3 fill-amber-400 text-amber-400" />
                                  <span>{staff.avgRating}</span>
                                  {staff.ratingsCount > 0 && (
                                    <span className="text-[10px] text-amber-500 font-semibold">({staff.ratingsCount})</span>
                                  )}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 font-medium text-gray-400 bg-gray-50 px-2.5 py-0.5 rounded-full border border-gray-200/60" title="No guest ratings received">
                                  <FiStar className="h-3 w-3 text-gray-300" />
                                  <span>—</span>
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-3 text-right">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setSelectedStaffModal(staff); }}
                                className="px-3 py-1 bg-gray-900 hover:bg-black text-white text-[11px] font-bold rounded-lg transition-all shadow-2xs"
                              >
                                View Requests ({staff.completed})
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════ */}
        {/*  TAB 4: ROOM ANALYTICS & INDIVIDUAL BREAKDOWN      */}
        {/* ══════════════════════════════════════════════════ */}
        {activeTab === 'rooms' && (
          <div className="space-y-4">
            
            {/* Top KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Active Rooms</p>
                <p className="text-2xl font-extrabold text-gray-900 mt-1">{roomAnalytics.activeRoomsCount}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Serviced rooms</p>
              </Card>

              <Card>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Avg Req / Room</p>
                <p className="text-2xl font-extrabold text-gray-900 mt-1">{roomAnalytics.avgRequestsPerRoom.toFixed(1)}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Requests per room</p>
              </Card>

              <Card>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Top Room</p>
                <p className="text-2xl font-extrabold text-gray-900 mt-1">{roomAnalytics.topDemandingRoom?.room_no || '—'}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{roomAnalytics.topDemandingRoom?.totalRequests || 0} reqs</p>
              </Card>

              <Card>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Slowest Room</p>
                <p className="text-2xl font-extrabold text-gray-900 mt-1">{roomAnalytics.slowestRoom?.room_no || '—'}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{formatHours(roomAnalytics.slowestRoom?.avgCompletionHours)} TAT</p>
              </Card>

              <Card>
                <p className="text-[10px] font-bold text-rose-600 uppercase tracking-wider">Maint Alerts</p>
                <p className="text-2xl font-extrabold text-rose-600 mt-1">{roomAnalytics.highMaintenanceCount}</p>
                <p className="text-[11px] text-rose-400 mt-0.5">3+ maint calls</p>
              </Card>
            </div>

            {/* Room Visual Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Top Active Rooms by Volume */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Top Active Rooms by Request Volume</h3>
                    <p className="text-xs text-gray-400">Completed and pending load per room</p>
                  </div>
                </div>
                <div className="h-64 w-full">
                  {roomAnalytics.top10ActiveChart.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-xs text-gray-400">No room data available</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={roomAnalytics.top10ActiveChart} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                        <XAxis dataKey="room" tick={{ fontSize: 11, fill: '#6b7280' }} interval={0} angle={-25} textAnchor="end" />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                        <Tooltip content={<MiniTooltip />} />
                        <Bar dataKey="Completed" fill="#6ec7c8" stackId="a" />
                        <Bar dataKey="Pending" fill="#f5c542" stackId="a" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Card>

              {/* Maintenance Hotspots */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Maintenance Hotspots</h3>
                    <p className="text-xs text-gray-400">Rooms with frequent maintenance calls</p>
                  </div>
                </div>
                <div className="h-64 w-full flex items-center justify-center">
                  {roomAnalytics.maintenanceHotspotsChart.length === 0 ? (
                    <div className="text-xs text-gray-400 flex flex-col items-center gap-1">
                      <FiCheckCircle className="h-6 w-6 text-teal-500" />
                      <span>No maintenance hotspots recorded</span>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={roomAnalytics.maintenanceHotspotsChart}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          innerRadius={45}
                          paddingAngle={3}
                        >
                          {roomAnalytics.maintenanceHotspotsChart.map((_, index) => (
                            <Cell key={index} fill={PALETTE[index % PALETTE.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<MiniTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Card>
            </div>

            {/* Individual Room Breakdown Directory Table (Integrated inside Room Analytics) */}
            <Card>
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-4 pb-3 border-b border-gray-100 flex-wrap">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <FiHome className="h-4 w-4 text-teal-600" /> Individual Room Breakdown Directory
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">Detailed history and operational metrics for every room</p>
                </div>

                {/* Filter Controls & Export */}
                <div className="flex items-center gap-2.5 flex-wrap w-full md:w-auto">
                  {/* Room Search */}
                  <div className="relative flex-1 md:w-52">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search room (e.g. 101)..."
                      value={roomSearchQuery}
                      onChange={(e) => setRoomSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-7 py-1.5 text-xs rounded-full border border-gray-200 bg-white text-gray-700 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-200"
                    />
                    {roomSearchQuery && (
                      <button
                        onClick={() => setRoomSearchQuery('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FiX className="h-3 w-3" />
                      </button>
                    )}
                  </div>

                  {/* Sort selector */}
                  <select
                    value={roomSortBy}
                    onChange={(e) => setRoomSortBy(e.target.value)}
                    className={SEL}
                  >
                    <option value="volume">Most Requests</option>
                    <option value="tat">Slowest TAT</option>
                    <option value="escalations">Most Escalations</option>
                    <option value="room">Room Number</option>
                  </select>

                  {/* High Maintenance Alert Toggle */}
                  <button
                    onClick={() => setRoomFilterAlertOnly(!roomFilterAlertOnly)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all border ${
                      roomFilterAlertOnly
                        ? 'bg-[rgba(255,194,194,0.35)] text-[#9d2d4e] border-[rgba(255,194,194,0.8)]'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <FiAlertTriangle className="h-3.5 w-3.5" />
                    Alerts {roomAnalytics.highMaintenanceCount > 0 && `(${roomAnalytics.highMaintenanceCount})`}
                  </button>

                  {/* Export CSV Button */}
                  <button
                    onClick={handleExportRoomAnalyticsCSV}
                    className="px-3.5 py-1.5 rounded-full text-xs font-bold text-gray-900 border bg-[rgba(110,199,200,0.25)] border-[rgba(110,199,200,0.5)] flex items-center gap-1.5 transition-all hover:bg-[rgba(110,199,200,0.35)]"
                  >
                    <FiDownload className="h-3.5 w-3.5" />
                    Export CSV
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead>
                    <tr className="bg-gray-50/60">
                      <th className="py-2.5 px-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Room</th>
                      <th className="py-2.5 px-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider">Total Reqs</th>
                      <th className="py-2.5 px-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status Breakdown</th>
                      <th className="py-2.5 px-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Avg TAT</th>
                      <th className="py-2.5 px-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Primary Dept</th>
                      <th className="py-2.5 px-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider">Escalations</th>
                      <th className="py-2.5 px-3 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider">Health Flag</th>
                      <th className="py-2.5 px-3 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {processedRoomList.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-10 text-center text-xs text-gray-400">
                          No rooms found matching current filter.
                        </td>
                      </tr>
                    ) : (
                      processedRoomList.map((r) => (
                        <tr key={r.room_no} className="hover:bg-gray-50/60 transition-colors group">
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              <span className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-extrabold text-gray-800">
                                {r.room_no}
                              </span>
                              <div>
                                <p className="text-xs font-bold text-gray-900">Room {r.room_no}</p>
                                <p className="text-[10px] text-gray-400">{r.topCategory}</p>
                              </div>
                            </div>
                          </td>

                          <td className="py-3 px-3 text-center font-extrabold text-xs text-gray-900">
                            {r.totalRequests}
                          </td>

                          <td className="py-3 px-3 min-w-[130px]">
                            <div className="flex items-center justify-between text-[10px] font-semibold mb-1">
                              <span className="text-[#175456]">{r.completedCount} done</span>
                              {r.pendingCount > 0 && <span className="text-[#9c6d05]">{r.pendingCount} pend</span>}
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden flex">
                              <div style={{ width: `${(r.completedCount / r.totalRequests) * 100}%` }} className="bg-[#6ec7c8] h-full" />
                              <div style={{ width: `${(r.pendingCount / r.totalRequests) * 100}%` }} className="bg-[#f5c542] h-full" />
                              <div style={{ width: `${(r.cancelledCount / r.totalRequests) * 100}%` }} className="bg-[#ffc2c2] h-full" />
                            </div>
                          </td>

                          <td className="py-3 px-3 font-mono text-xs font-semibold text-gray-700">
                            {formatHours(r.avgCompletionHours)}
                          </td>

                          <td className="py-3 px-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-700">
                              {r.topDept}
                            </span>
                          </td>

                          <td className="py-3 px-3 text-center">
                            {r.escalationCount > 0 ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border bg-[rgba(245,197,66,0.18)] text-[#9c6d05] border-[rgba(245,197,66,0.4)]">
                                {r.escalationCount} escalated
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">0</span>
                            )}
                          </td>

                          <td className="py-3 px-3 text-center">
                            {r.isHighMaintenance ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border bg-[rgba(255,194,194,0.35)] text-[#9d2d4e] border-[rgba(255,194,194,0.8)]">
                                <FiAlertTriangle className="h-3 w-3" /> Warning
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border bg-[rgba(110,199,200,0.15)] text-[#175456] border-[rgba(110,199,200,0.35)]">
                                <FiCheckCircle className="h-3 w-3" /> Normal
                              </span>
                            )}
                          </td>

                          <td className="py-3 px-3 text-right">
                            <button
                              onClick={() => setSelectedRoomModal(r)}
                              className="px-3 py-1 rounded-full text-xs font-bold border border-gray-200 text-gray-700 hover:bg-gray-100 transition-all flex items-center gap-1 ml-auto"
                            >
                              Details <FiChevronRight className="h-3 w-3" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ══════════════════════════════════════════════════ */}
        {/*  TAB 5: RATINGS & FEEDBACK (NEW DEDICATED SECTION) */}
        {/* ══════════════════════════════════════════════════ */}
        {activeTab === 'ratings' && (
          <div className="space-y-4">
            
            {/* Top Ratings KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              
              {/* Overall Star Rating */}
              <Card>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Overall Guest Rating</span>
                  <div className="p-2 rounded-xl bg-[rgba(245,197,66,0.2)] text-[#966b04]">
                    <FiAward className="h-4 w-4" />
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-4xl font-extrabold text-gray-900">{ratingsAnalytics.overallAvg}</span>
                  <span className="text-sm text-gray-400">/ 5.0</span>
                </div>
                <div className="flex items-center gap-1 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FiStar
                      key={star}
                      className={`h-4 w-4 ${
                        star <= Math.round(ratingsAnalytics.overallAvg)
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                  <span className="text-xs text-gray-400 ml-1.5 font-medium">({ratingsAnalytics.totalRated} ratings)</span>
                </div>
              </Card>

              {/* Satisfaction Rate */}
              <Card>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Guest Satisfaction</span>
                  <div className="p-2 rounded-xl bg-[rgba(110,199,200,0.2)] text-[#1e7577]">
                    <FiCheckCircle className="h-4 w-4" />
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-4xl font-extrabold text-gray-900">{ratingsAnalytics.positiveRate}%</span>
                </div>
                <p className="text-xs text-gray-500 mt-2 font-medium">4★ &amp; 5★ positive service ratings</p>
              </Card>

              {/* Rated Volume */}
              <Card>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Rated Requests</span>
                  <div className="p-2 rounded-xl bg-[rgba(193,100,216,0.18)] text-[#7a228f]">
                    <FiStar className="h-4 w-4" />
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-4xl font-extrabold text-gray-900">{ratingsAnalytics.totalRated}</span>
                  <span className="text-sm text-gray-400">submissions</span>
                </div>
                <p className="text-xs text-gray-500 mt-2 font-medium">Across active guest service tickets</p>
              </Card>
            </div>

            {/* Graphs Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              
              {/* Star Distribution Graph */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Rating Distribution (5★ to 1★)</h3>
                    <p className="text-xs text-gray-400">Count &amp; breakdown of star ratings submitted by guests</p>
                  </div>
                </div>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ratingsAnalytics.ratingDistData} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 10, fill: '#6b7280' }} />
                      <YAxis type="category" dataKey="star" tick={{ fontSize: 12, fill: '#374151', fontWeight: 700 }} width={50} />
                      <Tooltip content={<MiniTooltip />} />
                      <Bar dataKey="count" name="Ratings Count" radius={[0, 6, 6, 0]} barSize={16}>
                        {ratingsAnalytics.ratingDistData.map((_, i) => (
                          <Cell key={i} fill={i === 0 ? '#6ec7c8' : i === 1 ? '#f5c542' : i === 2 ? '#c164d8' : '#ffc2c2'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Department Rating Breakdown */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Average Rating by Department</h3>
                    <p className="text-xs text-gray-400">Department-wise guest satisfaction index</p>
                  </div>
                </div>
                <div className="h-64 w-full">
                  {ratingsAnalytics.deptRatingsData.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-xs text-gray-400">No departmental rating data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ratingsAnalytics.deptRatingsData} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                        <XAxis dataKey="department" tick={{ fontSize: 11, fill: '#6b7280' }} interval={0} angle={-20} textAnchor="end" />
                        <YAxis domain={[0, 5]} tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <Tooltip content={<MiniTooltip />} />
                        <Bar dataKey="avgRating" name="Avg Rating (Stars)" radius={[4, 4, 0, 0]} barSize={18}>
                          {ratingsAnalytics.deptRatingsData.map((_, i) => (
                            <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Card>
            </div>

            {/* Recent Rated Requests Log */}
            <Card>
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Recent Guest Feedback &amp; Ratings</h3>
                  <p className="text-xs text-gray-400">Latest completed service requests with feedback</p>
                </div>
                <button
                  type="button"
                  onClick={handleExportRatingsCSV}
                  className="px-2.5 py-1 rounded-full text-xs font-bold border border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100 transition-all flex items-center gap-1 shadow-2xs"
                  title="Export Ratings and Feedback CSV"
                >
                  <FiDownload className="h-3 w-3" />
                  <span>Export CSV</span>
                </button>
              </div>

              <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto pr-1">
                {ratingsAnalytics.ratingsList.length === 0 ? (
                  <p className="text-xs text-gray-400 py-8 text-center">No rated service requests recorded yet</p>
                ) : (
                  ratingsAnalytics.ratingsList.map((item) => (
                    <div key={item.id} className="py-3 flex items-start justify-between gap-3 text-xs">
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-gray-900">Room {item.room}</span>
                          <span className="text-[11px] text-gray-400">· {item.category}</span>
                          <span className="rounded-full px-2 py-0.2 text-[9px] font-bold border bg-[rgba(193,100,216,0.12)] text-[#7a228f] border-[rgba(193,100,216,0.3)]">
                            {item.department}
                          </span>
                        </div>
                        <p className="text-gray-600 text-[11px] italic">"{item.feedback}"</p>
                        <p className="text-[10px] text-gray-400">Handled by: <span className="font-medium text-gray-700">{item.staff}</span></p>
                      </div>

                      <div className="flex items-center gap-0.5 shrink-0 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
                        <FiStar className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                        <span className="font-extrabold text-amber-900 text-xs">{item.rating}.0</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>
        )}

        </div>
        {/* ── End of Exportable Dashboard Container ── */}

        {/* ── Room Detail Modal ───────────────────────────── */}
        {selectedRoomModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs animate-fade-in">
            <div className="bg-white rounded-[22px] shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-gray-100">
              {/* Modal Header */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center font-extrabold text-sm shadow-xs">
                    {selectedRoomModal.room_no}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Room {selectedRoomModal.room_no} Analytics</h3>
                    <p className="text-xs text-gray-400">
                      {selectedRoomModal.totalRequests} service requests recorded for this room
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedRoomModal(null)}
                  className="p-2 rounded-full hover:bg-gray-200/60 text-gray-400 hover:text-gray-700 transition-colors"
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto space-y-4 flex-1">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-[10px] text-gray-400 uppercase font-bold">Total</p>
                    <p className="text-lg font-extrabold text-gray-900">{selectedRoomModal.totalRequests}</p>
                  </div>
                  <div className="p-3 bg-[rgba(110,199,200,0.15)] rounded-xl border border-[rgba(110,199,200,0.35)]">
                    <p className="text-[10px] text-[#175456] uppercase font-bold">Completed</p>
                    <p className="text-lg font-extrabold text-[#175456]">{selectedRoomModal.completedCount}</p>
                  </div>
                  <div className="p-3 bg-[rgba(245,197,66,0.18)] rounded-xl border border-[rgba(245,197,66,0.4)]">
                    <p className="text-[10px] text-[#9c6d05] uppercase font-bold">Avg TAT</p>
                    <p className="text-lg font-extrabold text-[#9c6d05] font-mono">{formatHours(selectedRoomModal.avgCompletionHours)}</p>
                  </div>
                  <div className="p-3 bg-[rgba(193,100,216,0.15)] rounded-xl border border-[rgba(193,100,216,0.35)]">
                    <p className="text-[10px] text-[#7a228f] uppercase font-bold">Top Dept</p>
                    <p className="text-xs font-bold text-[#7a228f] mt-1 truncate">{selectedRoomModal.topDept}</p>
                  </div>
                </div>

                {selectedRoomModal.isHighMaintenance && (
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-2.5 text-xs text-rose-800">
                    <FiAlertTriangle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold">Maintenance Alert</p>
                      <p className="text-[11px] text-rose-700 mt-0.5">
                        This room has logged {selectedRoomModal.maintenanceCount} maintenance calls. Recommended to inspect equipment.
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                    Request History ({selectedRoomModal.requests.length})
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                    {selectedRoomModal.requests.map((req, idx) => (
                      <div
                        key={req._id || req.id || idx}
                        className="p-2.5 rounded-xl border border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3 text-xs"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-gray-900">{req.normalizedCategory || req.category}</span>
                            <span className={`px-2 py-0.2 rounded-full text-[9px] font-bold border ${
                              req.normalizedStatus === 'completed'
                                ? 'bg-[rgba(110,199,200,0.18)] text-[#175456] border-[rgba(110,199,200,0.4)]'
                                : 'bg-[rgba(245,197,66,0.18)] text-[#9c6d05] border-[rgba(245,197,66,0.4)]'
                            }`}>
                              {req.normalizedStatus}
                            </span>
                          </div>
                          {req.details && <p className="text-gray-500 text-[11px] truncate mt-0.5">{req.details}</p>}
                        </div>
                        <span className="text-[10px] text-gray-400 shrink-0 font-mono">
                          {formatDateTime(req.created_at || req.createdAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setSelectedRoomModal(null)}
                  className="px-4 py-1.5 rounded-full text-xs font-bold bg-gray-900 text-white hover:bg-black transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal: Staff Completed Requests Details ─────────────────── */}
        {selectedStaffModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-xs">
            <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]">
              
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white shrink-0">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center font-bold text-teal-800 text-sm">
                    {selectedStaffModal.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
                      {selectedStaffModal.name}
                    </h3>
                    <p className="text-xs text-gray-500 font-medium">
                      {selectedStaffModal.department} · {selectedStaffModal.completedRequestsList?.length || selectedStaffModal.completed} Completed Requests
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setSelectedStaffModal(null); setStaffModalSearch(''); }}
                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  <FiX size={18} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto space-y-4 flex-1">
                {/* Quick KPI Summary Strip */}
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-3 bg-emerald-50/70 rounded-xl border border-emerald-100">
                    <p className="text-[10px] text-emerald-800 uppercase font-bold">Total Completed</p>
                    <p className="text-xl font-extrabold text-emerald-700">{selectedStaffModal.completedRequestsList?.length || selectedStaffModal.completed}</p>
                  </div>
                  <div className="p-3 bg-amber-50/70 rounded-xl border border-amber-100">
                    <p className="text-[10px] text-amber-800 uppercase font-bold">Avg Speed</p>
                    <p className="text-xl font-extrabold text-amber-700 font-mono">{selectedStaffModal.avgCompletionMinutes} min</p>
                  </div>
                  <div className={`p-3 rounded-xl border ${selectedStaffModal.avgRating !== null ? 'bg-purple-50/70 border-purple-100' : 'bg-gray-50 border-gray-200'}`}>
                    <p className={`text-[10px] uppercase font-bold ${selectedStaffModal.avgRating !== null ? 'text-purple-800' : 'text-gray-500'}`}>CSAT Rating</p>
                    <p className={`text-xl font-extrabold flex items-center justify-center gap-1 ${selectedStaffModal.avgRating !== null ? 'text-purple-700' : 'text-gray-400'}`}>
                      <FiStar className={`h-4 w-4 ${selectedStaffModal.avgRating !== null ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                      <span>{selectedStaffModal.avgRating !== null ? selectedStaffModal.avgRating : '—'}</span>
                    </p>
                    <p className={`text-[10px] font-medium mt-0.5 text-center ${selectedStaffModal.avgRating !== null ? 'text-purple-600' : 'text-gray-400'}`}>
                      {selectedStaffModal.avgRating !== null
                        ? `${selectedStaffModal.ratingsCount || 1} review${(selectedStaffModal.ratingsCount || 1) === 1 ? '' : 's'}`
                        : 'No reviews yet'}
                    </p>
                  </div>
                </div>

                {/* Search / Filter in Modal */}
                <div className="flex items-center gap-2 px-3.5 py-2 bg-gray-50 rounded-xl border border-gray-200 text-xs">
                  <FiSearch className="text-gray-400 h-4 w-4 shrink-0" />
                  <input
                    type="text"
                    value={staffModalSearch}
                    onChange={(e) => setStaffModalSearch(e.target.value)}
                    placeholder="Search by Request ID, Room No, Category, or details..."
                    className="bg-transparent border-0 text-xs text-gray-800 w-full focus:outline-none placeholder-gray-400"
                  />
                  {staffModalSearch && (
                    <button onClick={() => setStaffModalSearch('')} className="text-gray-400 hover:text-gray-600">
                      <FiX size={14} />
                    </button>
                  )}
                </div>

                {/* Individual Requests Line-wise list */}
                <div>
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2.5">
                    Completed Requests History ({selectedStaffModal.completedRequestsList?.length || 0})
                  </h4>

                  {(!selectedStaffModal.completedRequestsList || selectedStaffModal.completedRequestsList.length === 0) ? (
                    <div className="text-center py-8 text-xs text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      No individual request logs available for this staff in the selected filters.
                    </div>
                  ) : (
                    <div className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
                      {selectedStaffModal.completedRequestsList
                        .filter((req) => {
                          if (!staffModalSearch.trim()) return true;
                          const q = staffModalSearch.trim().toLowerCase();
                          return (
                            String(req.id || '').toLowerCase().includes(q) ||
                            String(req.room_no || '').toLowerCase().includes(q) ||
                            String(req.guest_name || '').toLowerCase().includes(q) ||
                            String(req.category || '').toLowerCase().includes(q) ||
                            String(req.details || '').toLowerCase().includes(q)
                          );
                        })
                        .map((req, idx) => (
                          <div
                            key={req.id || idx}
                            className="p-3.5 rounded-xl border border-gray-200 bg-white hover:border-teal-300 hover:shadow-xs transition-all flex flex-col gap-2"
                          >
                            <div className="flex items-start justify-between gap-3 text-xs">
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-[11px] font-mono">
                                    #{String(req.id || '').slice(0, 8)}
                                  </span>
                                  <span className="font-extrabold text-gray-900">
                                    {req.room_no && req.room_no !== '—' ? `Room ${req.room_no}` : ''} {req.guest_name ? `· ${req.guest_name}` : ''}
                                  </span>
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    Completed
                                  </span>
                                  {req.rating && (
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-0.5">
                                      <FiStar className="h-3 w-3 fill-amber-400 text-amber-400" /> {req.rating}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs font-semibold text-teal-700 mt-1.5">
                                  {req.category}
                                </p>
                                {req.details && (
                                  <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">
                                    {req.details}
                                  </p>
                                )}
                              </div>

                              {req.diffMinutes !== null && (
                                <div className="text-right shrink-0">
                                  <span className="text-[10px] text-gray-400 block uppercase font-bold">Turnaround</span>
                                  <span className="text-xs font-extrabold text-gray-900 font-mono">
                                    {req.diffMinutes} min
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="pt-2 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400 font-medium">
                              <span>Created: {formatDateTime(req.created_at)}</span>
                              <span>Completed: {formatDateTime(req.completed_at)}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => { setSelectedStaffModal(null); setStaffModalSearch(''); }}
                  className="px-4 py-1.5 rounded-full text-xs font-bold bg-gray-900 text-white hover:bg-black transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal: Department Requests Details ───────────────────────── */}
        {selectedDeptModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-xs">
            <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]">
              
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white shrink-0">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center font-bold text-teal-800 text-sm">
                    {selectedDeptModal.department.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
                      {selectedDeptModal.department}
                    </h3>
                    <p className="text-xs text-gray-500 font-medium">
                      {selectedDeptModal.total} Total Requests · {selectedDeptModal.completed} Completed · {selectedDeptModal.pending} Pending
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setSelectedDeptModal(null); setDeptModalSearch(''); }}
                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  <FiX size={18} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto space-y-4 flex-1">
                {/* Quick KPI Summary Strip */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-[10px] text-gray-500 uppercase font-bold">Total</p>
                    <p className="text-xl font-extrabold text-gray-900">{selectedDeptModal.total}</p>
                  </div>
                  <div className="p-3 bg-emerald-50/70 rounded-xl border border-emerald-100">
                    <p className="text-[10px] text-emerald-800 uppercase font-bold">Completed</p>
                    <p className="text-xl font-extrabold text-emerald-700">{selectedDeptModal.completed}</p>
                  </div>
                  <div className="p-3 bg-amber-50/70 rounded-xl border border-amber-100">
                    <p className="text-[10px] text-amber-800 uppercase font-bold">Pending</p>
                    <p className="text-xl font-extrabold text-amber-700">{selectedDeptModal.pending}</p>
                  </div>
                  <div className="p-3 bg-teal-50/70 rounded-xl border border-teal-100">
                    <p className="text-[10px] text-teal-800 uppercase font-bold">Avg Speed</p>
                    <p className="text-xl font-extrabold text-teal-700 font-mono">{formatHours(selectedDeptModal.avgCompletionHours)}</p>
                  </div>
                </div>

                {/* Search / Filter in Modal */}
                <div className="flex items-center gap-2 px-3.5 py-2 bg-gray-50 rounded-xl border border-gray-200 text-xs">
                  <FiSearch className="text-gray-400 h-4 w-4 shrink-0" />
                  <input
                    type="text"
                    value={deptModalSearch}
                    onChange={(e) => setDeptModalSearch(e.target.value)}
                    placeholder="Search requests by ID, Room, Category, Staff, or Details..."
                    className="bg-transparent border-0 text-xs text-gray-800 w-full focus:outline-none placeholder-gray-400"
                  />
                  {deptModalSearch && (
                    <button onClick={() => setDeptModalSearch('')} className="text-gray-400 hover:text-gray-600">
                      <FiX size={14} />
                    </button>
                  )}
                </div>

                {/* Individual Requests Line-wise list */}
                <div>
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2.5">
                    Department Service Requests ({selectedDeptModal.requests?.length || 0})
                  </h4>

                  {(!selectedDeptModal.requests || selectedDeptModal.requests.length === 0) ? (
                    <div className="text-center py-8 text-xs text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      No requests found for this department in the selected filters.
                    </div>
                  ) : (
                    <div className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
                      {selectedDeptModal.requests
                        .filter((req) => {
                          if (!deptModalSearch.trim()) return true;
                          const q = deptModalSearch.trim().toLowerCase();
                          return (
                            String(req.id || '').toLowerCase().includes(q) ||
                            String(req.room_no || '').toLowerCase().includes(q) ||
                            String(req.guest_name || '').toLowerCase().includes(q) ||
                            String(req.category || '').toLowerCase().includes(q) ||
                            String(req.staff_name || '').toLowerCase().includes(q) ||
                            String(req.details || '').toLowerCase().includes(q)
                          );
                        })
                        .map((req, idx) => {
                          let badgeBg = 'bg-amber-50 text-amber-700 border-amber-200';
                          if (req.status === 'completed') badgeBg = 'bg-emerald-50 text-emerald-700 border-emerald-200';
                          else if (req.status === 'dnd') badgeBg = 'bg-slate-100 text-slate-700 border-slate-200';
                          else if (req.status === 'critical') badgeBg = 'bg-rose-50 text-rose-700 border-rose-200';
                          else if (req.status === 'dispute') badgeBg = 'bg-purple-50 text-purple-700 border-purple-200';

                          return (
                            <div
                              key={req.id || idx}
                              className="p-3.5 rounded-xl border border-gray-200 bg-white hover:border-teal-300 hover:shadow-xs transition-all flex flex-col gap-2"
                            >
                              <div className="flex items-start justify-between gap-3 text-xs">
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-[11px] font-mono">
                                      #{String(req.id || '').slice(0, 8)}
                                    </span>
                                    <span className="font-extrabold text-gray-900">
                                      {req.room_no && req.room_no !== '—' ? `Room ${req.room_no}` : ''} {req.guest_name ? `· ${req.guest_name}` : ''}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${badgeBg}`}>
                                      {req.status === 'critical' ? 'Critical Fix' : req.status === 'dispute' ? 'Service Dispute' : req.status.toUpperCase()}
                                    </span>
                                    {req.rating && (
                                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-0.5">
                                        <FiStar className="h-3 w-3 fill-amber-400 text-amber-400" /> {req.rating}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs font-semibold text-teal-700 mt-1.5">
                                    {req.category} {req.staff_name ? `(Staff: ${req.staff_name})` : ''}
                                  </p>
                                  {req.details && (
                                    <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">
                                      {req.details}
                                    </p>
                                  )}
                                  {req.metadata?.dispute_note && (
                                    <div className="mt-1.5 p-2 rounded-lg bg-orange-50 border border-orange-200 text-xs">
                                      <span className="font-bold text-orange-950">Dispute Note:</span>{' '}
                                      <span className="text-gray-800 italic">"{req.metadata.dispute_note}"</span>
                                      {req.metadata?.dispute_note_by_user_name && (
                                        <span className="text-gray-500 text-[10px] ml-1">({req.metadata.dispute_note_by_user_name})</span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {req.diffMinutes !== null && req.status === 'completed' && (
                                  <div className="text-right shrink-0">
                                    <span className="text-[10px] text-gray-400 block uppercase font-bold">Turnaround</span>
                                    <span className="text-xs font-extrabold text-gray-900 font-mono">
                                      {req.diffMinutes} min
                                    </span>
                                  </div>
                                )}
                              </div>

                              <div className="pt-2 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400 font-medium">
                                <span>Created: {formatDateTime(req.created_at)}</span>
                                {req.status === 'completed' && <span>Completed: {formatDateTime(req.completed_at)}</span>}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => { setSelectedDeptModal(null); setDeptModalSearch(''); }}
                  className="px-4 py-1.5 rounded-full text-xs font-bold bg-gray-900 text-white hover:bg-black transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {isExportingPdf && (
          <div className="fixed inset-0 z-[99999] bg-gray-900/85 backdrop-blur-sm flex flex-col items-center justify-center text-white p-4" style={{ pointerEvents: 'all' }}>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-400 border-t-transparent shadow-lg mb-5" />
            <h3 className="text-xl font-black tracking-wide uppercase mb-1">Generating PDF Report</h3>
            <p className="text-sm text-gray-300 text-center max-w-md">
              Building all 5 sections: Overview, Department Performance, Staff Analytics, Room Analytics &amp; Ratings…
            </p>
            <span className="mt-3 px-4 py-1.5 bg-teal-500/20 text-teal-300 border border-teal-500/30 rounded-full text-xs font-semibold">
              {filterSummaryText}
            </span>
            <p className="mt-2 text-xs text-gray-500">This may take a few seconds…</p>
          </div>
        )}

      </div>
    </Layout>
  );
}

export default Analytics;
